const LEGACY_CART_STORAGE_KEY = 'parent_multi_organization_cart';
const CART_STORAGE_PREFIX = 'parent_multi_organization_cart:user:';
const CART_CHANGED_EVENT = 'parent-cart-changed';

// Cart is scoped per authenticated parent user id to prevent cross-user cart
// leakage on a shared browser. Until setParentCartOwner() is called on login,
// reads return an empty cart and writes are no-ops, so an unauthenticated or
// pre-login state can never read another user's cart.
let currentUserId = null;

const getParentCartStorageKey = () => (currentUserId ? `${CART_STORAGE_PREFIX}${currentUserId}` : null);

export const setParentCartOwner = (userId) => {
  const nextId = userId !== null && userId !== undefined && String(userId).trim() !== ''
    ? String(userId).trim()
    : null;
  currentUserId = nextId;
};

export const clearLegacyParentCart = () => {
  try {
    localStorage.removeItem(LEGACY_CART_STORAGE_KEY);
  } catch (e) {
    // ignore — legacy cleanup is best-effort
  }
};

const generateCartId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `cart-${crypto.randomUUID()}`;
  }

  return `cart-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createCartItemId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

  const normalizeCartItem = (item) => {
  const tenantId = item?.tenant_id ?? item?.school_id ?? item?.tenant?.id ?? null;
  const tenantSlug = String(item?.tenant_slug || item?.school_slug || item?.tenant?.slug || '').trim();
  const tenantName = String(item?.tenant_name || item?.school_name || item?.tenant?.name || '').trim();
  const tenantLogo = String(item?.tenant_logo || item?.school_logo || item?.tenant?.logo || '').trim();
  const tenantType = String(item?.tenant_type || item?.organization_type || item?.school_type || item?.tenant?.organization_type || '').trim();
  const quantity = Math.max(1, Number(item?.quantity || 1));
  const unitPrice = Number(item?.unit_price || item?.price || 0);
  const lineTotal = Number(item?.line_total || 0) || (unitPrice * quantity);
  const campaignId = item?.campaign_id ?? item?.campaign?.id ?? null;
  const campaign = item?.campaign || (campaignId ? { id: campaignId } : null);

  return {
    cart_item_id: String(item?.cart_item_id || createCartItemId()),
    tenant_id: tenantId,
    tenant_slug: tenantSlug,
    tenant_name: tenantName,
    tenant_logo: tenantLogo,
    tenant_type: tenantType,
    product_id: item?.product_id ?? null,
    product_name: String(item?.product_name || '').trim(),
    product_image: String(item?.product_image || '').trim(),
    child_name: String(item?.child_name || '').trim(),
    class_name: String(item?.class_name || '').trim(),
    sports_house: String(item?.sports_house || '').trim(),
    sleeve_type: String(item?.sleeve_type || 'short').toLowerCase() === 'long' ? 'long' : 'short',
    variation_id: item?.variation_id ?? null,
    size: String(item?.size || '').trim(),
    quantity,
    unit_price: unitPrice,
    line_total: lineTotal,
    campaign_id: campaign ? Number(campaign.id) : null,
    campaign_name: String(campaign?.name || '').trim(),
    campaign_payment_policy: String(campaign?.payment_policy || '').trim(),
    campaign_require_funding_code: Boolean(campaign?.require_funding_code ?? false),
    campaign_allocation_type: String(campaign?.allocation_type || '').trim(),
    funding_code: String(item?.funding_code || '').trim() || undefined,
  };
};

const cartItemSignature = (item = {}) => {
  const tenantSlug = String(item?.tenant_slug || '').trim().toLowerCase();
  const tenantId = item?.tenant_id !== null && item?.tenant_id !== undefined ? String(item.tenant_id).trim() : '';
  const tenantPart = tenantSlug !== '' ? `slug:${tenantSlug}` : `tenant:${tenantId}`;

  return [
    tenantPart,
    String(item?.campaign_id ?? ''),
    String(item?.product_id ?? ''),
    String(item?.variation_id ?? ''),
    String(item?.size || '').trim().toUpperCase(),
    String(item?.sleeve_type || 'short').trim().toLowerCase(),
    String(item?.child_name || '').trim().toLowerCase(),
    String(item?.class_name || '').trim().toLowerCase(),
    String(item?.sports_house || '').trim().toLowerCase(),
  ].join('|');
};

const dedupeCartItems = (items = []) => {
  const merged = new Map();

  items.forEach((rawItem) => {
    const item = normalizeCartItem(rawItem);
    const signature = cartItemSignature(item);
    const existing = merged.get(signature);

    if (!existing) {
      merged.set(signature, item);
      return;
    }

    const quantity = Math.max(1, Number(existing.quantity || 1)) + Math.max(1, Number(item.quantity || 1));
    const unitPrice = Number(existing.unit_price || item.unit_price || 0);

    merged.set(signature, {
      ...existing,
      quantity,
      unit_price: unitPrice,
      line_total: unitPrice * quantity,
    });
  });

  return Array.from(merged.values());
};

const safeParseCart = (rawValue) => {
  if (!rawValue) {
    return { cart_id: '', items: [] };
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object') {
      return { cart_id: '', items: [] };
    }

    const cartId = String(parsed.cart_id || '').trim();
    const rawItems = Array.isArray(parsed.items) ? parsed.items : [];
    const usedIds = new Set();
    const normalizedItems = rawItems.map((item) => {
      const normalized = normalizeCartItem(item);
      const existingId = String(normalized.cart_item_id || '').trim();

      if (!existingId || usedIds.has(existingId)) {
        const nextId = createCartItemId();
        usedIds.add(nextId);
        return { ...normalized, cart_item_id: nextId };
      }

      usedIds.add(existingId);
      return normalized;
    });

    return {
      cart_id: cartId,
      items: dedupeCartItems(normalizedItems),
    };
  } catch (error) {
    return { cart_id: '', items: [] };
  }
};

const readCart = () => {
  const storageKey = getParentCartStorageKey();
  if (!storageKey) {
    return { cart_id: '', items: [] };
  }
  return safeParseCart(localStorage.getItem(storageKey));
};

const writeCart = (cart) => {
  const storageKey = getParentCartStorageKey();
  if (!storageKey) {
    return { cart_id: '', items: [] };
  }

  const sourceItems = Array.isArray(cart?.items) ? cart.items : [];
  const normalizedCart = {
    cart_id: String(cart?.cart_id || '').trim(),
    items: dedupeCartItems(sourceItems),
  };

  localStorage.setItem(storageKey, JSON.stringify(normalizedCart));
  window.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT, { detail: normalizedCart }));
  return normalizedCart;
};

const normalizeTenantKey = (item = {}) => {
  const tenantSlug = String(item?.tenant_slug || '').trim().toLowerCase();
  if (tenantSlug !== '') {
    return `slug:${tenantSlug}`;
  }

  const tenantId = item?.tenant_id;
  if (tenantId !== null && tenantId !== undefined && String(tenantId).trim() !== '') {
    return `tenant:${tenantId}`;
  }

  const tenantName = String(item?.tenant_name || '').trim().toLowerCase();
  if (tenantName !== '') {
    return `name:${tenantName}`;
  }

  return 'unknown';
};

export const getParentCart = () => readCart();

export const getParentCartItems = () => readCart().items;

export const getParentCartId = () => readCart().cart_id;

export const getParentCartCount = () => readCart().items.length;

export const calculateCartTotal = (items = []) => items.reduce((sum, item) => sum + Number(item?.line_total || 0), 0);

export const calculateCartSubtotal = (items = []) => calculateCartTotal(items);

export const calculatePlatformFee = (subtotal = 0) => Number(subtotal || 0) * 0;

export const calculateCartSummary = (items = []) => {
  const subtotal = calculateCartSubtotal(items);
  const platformFee = calculatePlatformFee(subtotal);

  return {
    subtotal,
    platform_fee: platformFee,
    total: subtotal + platformFee,
  };
};

export const groupCartItemsByOrganization = (items = []) => {
  return items.reduce((groups, item) => {
    const tenantKey = normalizeTenantKey(item);
    if (!groups[tenantKey]) {
      groups[tenantKey] = {
        tenant_id: item?.tenant_id ?? null,
        tenant_slug: String(item?.tenant_slug || '').trim(),
        tenant_name: String(item?.tenant_name || item?.tenant_slug || 'Organization').trim(),
        tenant_logo: String(item?.tenant_logo || '').trim(),
        tenant_type: String(item?.tenant_type || '').trim(),
        items: [],
      };
    }

    groups[tenantKey].items.push(item);
    return groups;
  }, {});
};

export const addItemsToParentCart = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return readCart();
  }

  const nextItems = items.map(normalizeCartItem);
  const currentCart = readCart();
  const cartId = currentCart.cart_id || generateCartId();

  return writeCart({
    cart_id: cartId,
    items: [...currentCart.items, ...nextItems],
  });
};

export const updateParentCartItem = (cartItemId, patch = {}) => {
  const currentCart = readCart();
  const nextItems = currentCart.items.map((item) => {
    if (String(item.cart_item_id) !== String(cartItemId)) {
      return item;
    }

    const merged = normalizeCartItem({ ...item, ...patch, cart_item_id: item.cart_item_id });
    return {
      ...item,
      ...merged,
    };
  });

  return writeCart({ cart_id: currentCart.cart_id, items: nextItems });
};

export const removeParentCartItem = (cartItemId) => {
  if (cartItemId === null || cartItemId === undefined || String(cartItemId).trim() === '') {
    return readCart();
  }

  const currentCart = readCart();
  return writeCart({
    cart_id: currentCart.cart_id,
    items: currentCart.items.filter((item) => String(item.cart_item_id) !== String(cartItemId)),
  });
};

export const clearParentCart = () => {
  const storageKey = getParentCartStorageKey();
  if (storageKey) {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      // ignore
    }
  }
  return writeCart({ cart_id: '', items: [] });
};

export const subscribeParentCart = (handler) => {
  const listener = () => handler(readCart());
  window.addEventListener(CART_CHANGED_EVENT, listener);
  window.addEventListener('storage', listener);

  return () => {
    window.removeEventListener(CART_CHANGED_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
};

export const prepareParentCartPayload = (items = [], cartId = '', paymentOptions = {}) => {
  const paymentMethod = String(paymentOptions.payment_method || 'online_gateway').trim();
  const paymentSource = String(paymentOptions.payment_source || '').trim();
  let paidBy = String(paymentOptions.paid_by || '').trim();

  const firstItem = items[0] || {};
  const tenantName = String(firstItem.tenant_name || '').trim();

  if (!paidBy && (paymentMethod === 'organization' || paymentMethod === 'school')) {
    paidBy = tenantName;
  }

  return {
    cart_id: String(cartId || '').trim(),
    auto_pay: true,
    payment_method: paymentMethod,
    payment_source: paymentSource || undefined,
    paid_by: paidBy || undefined,
    items: items.map((item) => {
      const payloadItem = {
        product_id: Number(item.product_id),
        tenant_slug: String(item.tenant_slug || '').trim(),
        variation_id: item.variation_id ? Number(item.variation_id) : null,
        child_name: String(item.child_name || '').trim(),
        class: String(item.class_name || '').trim(),
        sports_house: String(item.sports_house || '').trim() || undefined,
        sleeve_type: String(item.sleeve_type || 'short').toLowerCase() === 'long' ? 'long' : 'short',
        size: String(item.size || '').trim(),
        quantity: Number(item.quantity || 1),
      };

      if (item?.campaign_id) {
        payloadItem.campaign_id = Number(item.campaign_id);
      }
      if (item?.funding_code) {
        payloadItem.funding_code = String(item.funding_code).trim();
      }

      return payloadItem;
    }),
  };
};

const parentCartUtils = {
  getParentCart,
  getParentCartItems,
  getParentCartId,
  getParentCartCount,
  addItemsToParentCart,
  updateParentCartItem,
  removeParentCartItem,
  clearParentCart,
  calculateCartTotal,
  calculateCartSubtotal,
  calculatePlatformFee,
  calculateCartSummary,
  groupCartItemsByOrganization,
  subscribeParentCart,
  prepareParentCartPayload,
  setParentCartOwner,
  clearLegacyParentCart,
};

export default parentCartUtils;
